import { useCallback, useEffect, useMemo, useState } from "react";
import { PlatformViewBanner, ManageTenantBtn, TenantColHeader, TenantColCell, usePlatformView } from "@/components/platform-view/PlatformViewShell";
import { BulkActionBar, RowCheckbox, SelectAllCheckbox, useBulkSelection } from "@/components/bulk/BulkSelection";
import { EmptyTrashButton, PurgeRowDialog, useCanPurge } from "@/components/trash/PurgeControls";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Trash2, Undo2, Info, Filter, Search } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { fmtDateTime } from "@/utils/datetime";

type TrashItem = {
    id: string;
    batch_id: string;
    tenant_id: string;
    entity_type: string;
    entity_id: string;
    label_key: string;
    summary: Record<string, unknown>;
    status: string;
    is_finance?: boolean;
    hard_purge_eligible?: boolean;
    deleted_at: string;
    deleted_by: string;
    purge_after: string;
};

const ENTITY_TYPES = [
    "lead", "person", "program", "group", "room",
    // Phase TRASH-2 — finance entities.
    "invoice", "payment", "expense", "payroll_run", "other_income",
] as const;

function daysRemaining(purgeAfter: string): number {
    const ms = new Date(purgeAfter).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function summaryLabel(item: TrashItem): string {
    const s = (item.summary || {}) as Record<string, unknown>;
    return (s.name as string) || (s.label as string) || item.entity_id;
}

export default function Trash() {
    const { t } = useTranslation();
    const { filterTenantId, isPlatformView, tenantName } = usePlatformView();
    const canPurge = useCanPurge();
    const [confirmPurge, setConfirmPurge] = useState<TrashItem | null>(null);
    const [items, setItems] = useState<TrashItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [entityFilter, setEntityFilter] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<string>("in_trash");
    const [search, setSearch] = useState<string>("");
    const [confirmRestore, setConfirmRestore] = useState<TrashItem | null>(null);
    const [restoring, setRestoring] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, string> = { status: statusFilter };
            if (entityFilter) params.entity_type = entityFilter;
            if (search) params.search = search;
            if (filterTenantId) params.filter_tenant_id = filterTenantId;
            const r = await api.get("/trash", { params });
            setItems(r.data.items || []);
        } catch (e: any) {
            if (e?.response?.status === 403) {
                toast.error(t("trash.forbidden"));
                setItems([]);
            } else {
                toast.error(formatApiError(e));
            }
        } finally {
            setLoading(false);
        }
    }, [entityFilter, statusFilter, search, filterTenantId, t]);

    useEffect(() => { void load(); }, [load]);

    const filtered = useMemo(() => items, [items]);
    const purgeable = (it: TrashItem) => it.status === "in_trash" || it.status === "archived_retained";
    const selection = useBulkSelection(`${statusFilter}|${entityFilter}|${filterTenantId}`);
    const bulkRows = useMemo(() => filtered.filter(purgeable).map(it => ({ id: it.batch_id, tenant_id: it.tenant_id, tenant_name: tenantName(it.tenant_id) })), [filtered, tenantName]);
    const showSel = canPurge && bulkRows.length > 0;
    const colCount = (isPlatformView ? 8 : 7) + (showSel ? 1 : 0);
    const scopeLabel = isPlatformView ? (filterTenantId ? tenantName(filterTenantId) : t("trash.purge.scope_all")) : t("trash.purge.scope_own");

    const doRestore = async () => {
        if (!confirmRestore) return;
        setRestoring(true);
        try {
            await api.post(`/trash/${confirmRestore.batch_id}/restore`);
            toast.success(t("trash.restored"));
            setConfirmRestore(null);
            await load();
        } catch (e: any) {
            toast.error(formatApiError(e) || t("trash.restore_failed"));
        } finally {
            setRestoring(false);
        }
    };

    return (
        <div className="space-y-6 p-6" data-testid="trash-page"><PlatformViewBanner />
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-semibold flex items-center gap-3">
                        <Trash2 className="w-7 h-7 text-primary" />
                        {t("trash.title")}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        {t("trash.description")}
                    </p>
                </div>
                {canPurge && (
                    <EmptyTrashButton scopeLabel={scopeLabel} tenantId={isPlatformView ? filterTenantId || undefined : undefined}
                        count={bulkRows.length} onDone={load} />
                )}
            </div>

            <section className="rounded-xl border border-border bg-card">
                <div className="px-5 py-3 border-b border-border">
                    <h2 className="text-base font-medium flex items-center gap-2">
                        <Filter className="w-4 h-4" /> {t("trash.filters")}
                    </h2>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="relative md:col-span-2">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            data-testid="trash-search-input"
                            className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-background text-sm"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("trash.search_placeholder")}
                        />
                    </div>
                    <select
                        data-testid="trash-entity-filter"
                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm"
                        value={entityFilter}
                        onChange={(e) => setEntityFilter(e.target.value)}
                    >
                        <option value="">{t("trash.entity_all")}</option>
                        {ENTITY_TYPES.map((et) => (
                            <option key={et} value={et}>{t(`trash.entity.${et}`)}</option>
                        ))}
                    </select>
                    <select
                        data-testid="trash-status-filter"
                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="in_trash">{t("trash.status.in_trash")}</option>
                        <option value="archived_retained">{t("trash.status.archived_retained")}</option>
                        <option value="restored">{t("trash.status.restored")}</option>
                        <option value="purged">{t("trash.status.purged")}</option>
                    </select>
                </div>
            </section>

            <section className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/30 text-muted-foreground">
                            <tr className="text-left">
                                {showSel && <th className="px-4 py-3 w-10"><SelectAllCheckbox selection={selection} rows={bulkRows} testid="trash-select-all" /></th>}
                                <th className="px-4 py-3 font-medium">{t("trash.col_entity")}</th>
                                <TenantColHeader />
                                <th className="px-4 py-3 font-medium">{t("trash.col_type")}</th>
                                <th className="px-4 py-3 font-medium">{t("trash.col_deleted_at")}</th>
                                <th className="px-4 py-3 font-medium">{t("trash.col_deleted_by")}</th>
                                <th className="px-4 py-3 font-medium">{t("trash.col_days_left")}</th>
                                <th className="px-4 py-3 font-medium">{t("trash.col_status")}</th>
                                <th className="px-4 py-3 font-medium text-right">{t("trash.col_actions")}</th>
                            </tr>
                        </thead>
                        <tbody data-testid="trash-list">
                            {loading && (
                                <tr><td colSpan={colCount} className="px-4 py-8 text-center text-muted-foreground">{t("common.loading")}</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={colCount} className="px-4 py-10 text-center text-muted-foreground">
                                    <span className="inline-flex items-center gap-2"><Info className="w-4 h-4" />{t("trash.empty")}</span>
                                </td></tr>
                            )}
                            {filtered.map((it) => {
                                const days = daysRemaining(it.purge_after);
                                return (
                                    <tr key={it.id} className="border-t border-border hover:bg-muted/20"
                                        data-testid={`trash-row-${it.entity_id}`}>
                                        {showSel && <td className="px-4 py-3">{purgeable(it) && (
                                            <RowCheckbox selection={selection} testid={`trash-select-${it.entity_id}`}
                                                item={{ id: it.batch_id, tenant_id: it.tenant_id, tenant_name: tenantName(it.tenant_id) }} />
                                        )}</td>}
                                        <td className="px-4 py-3 font-medium">{summaryLabel(it)}</td>
                                        <TenantColCell tenantId={it.tenant_id} testId={`tenant-cell-trash-${it.entity_id}`} />
                                        <td className="px-4 py-3">{t(`trash.entity.${it.entity_type}`, it.entity_type)}</td>
                                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDateTime(it.deleted_at)}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{(it.deleted_by || "").slice(0, 8)}…</td>
                                        <td className="px-4 py-3">
                                            {/* Phase TRASH-2: archive-only rows show a dedicated
                                                "never purged" label instead of a days-left counter
                                                so operators never wait for a purge that won't come. */}
                                            {it.status !== "in_trash" ? "—"
                                                : it.hard_purge_eligible === false ? (
                                                    <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--text-muted)" }} data-testid={`trash-never-purged-${it.entity_id}`}>
                                                        {t("trash.days_left_not_purged")}
                                                    </span>
                                                ) : (
                                                    <span className={days <= 3 ? "text-red-500 font-medium" : ""}>
                                                        {t("trash.days_left", { count: days })}
                                                    </span>
                                                )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={
                                                "inline-flex px-2 py-0.5 rounded-md text-xs font-medium " +
                                                (it.status === "in_trash"
                                                    ? "bg-amber-500/15 text-amber-500"
                                                    : it.status === "restored"
                                                        ? "bg-emerald-500/15 text-emerald-500"
                                                        : it.status === "archived_retained"
                                                            ? "bg-sky-500/15 text-sky-500"
                                                            : "bg-muted text-muted-foreground")
                                            }
                                                data-testid={`trash-status-${it.entity_id}`}
                                            >{t(`trash.status.${it.status}`, it.status)}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <ManageTenantBtn tenantId={(it as any).tenant_id} testId={`manage-trash-${it.entity_id}`} nextPath="/trash" />
                                                {/* Restore is offered for in_trash *and* archived_retained
                                                    rows — an operator can always change their mind about
                                                    a finance archive. */}
                                                {(it.status === "in_trash" || it.status === "archived_retained") && (
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                                                        data-testid={`trash-restore-${it.entity_id}`}
                                                        onClick={() => setConfirmRestore(it)}
                                                    >
                                                        <Undo2 className="w-4 h-4" />
                                                        {t("trash.restore")}
                                                    </button>
                                                )}
                                                {canPurge && purgeable(it) && (
                                                    <button type="button" data-testid={`trash-purge-${it.entity_id}`} onClick={() => setConfirmPurge(it)}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm transition-colors hover:bg-[var(--error-bg)]"
                                                        style={{ borderColor: "hsl(var(--destructive))", color: "hsl(var(--destructive))" }}>
                                                        <Trash2 className="w-4 h-4" />
                                                        {t("trash.purge.row_btn")}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {confirmPurge && (
                <PurgeRowDialog batchId={confirmPurge.batch_id} name={summaryLabel(confirmPurge)}
                    onClose={() => setConfirmPurge(null)} onDone={load} />
            )}
            {canPurge && (
                <BulkActionBar selection={selection} entityType="person" mode="purge" platform={isPlatformView}
                    onDone={load} testidPrefix="trash-bulk" />
            )}
            {confirmRestore && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
                    onClick={() => !restoring && setConfirmRestore(null)}
                    data-testid="trash-restore-dialog"
                >
                    <div className="bg-card border border-border rounded-xl p-6 shadow-2xl max-w-md w-full mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold mb-2">{t("trash.restore_confirm_title")}</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            {t("trash.restore_confirm_body", { name: summaryLabel(confirmRestore) })}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button type="button"
                                className="px-4 py-2 rounded-md border border-border text-sm hover:bg-accent transition-colors"
                                data-testid="trash-restore-cancel"
                                onClick={() => setConfirmRestore(null)}>{t("common.cancel")}</button>
                            <button type="button"
                                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
                                data-testid="trash-restore-confirm"
                                disabled={restoring} onClick={doRestore}>
                                {t("trash.restore")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
